import React from 'react';

import { Badge, Group, Stack, Text } from '@mantine/core';
import Drawer from 'components/Drawer';
import dayjs from 'dayjs';

import { formatCoachLabel, formatEventTime } from './cards/resolveCardVisuals';
import type { AthleteCalendarEventItem } from './types';

const DRAWER_WIDTH = 500;

interface AthleteEventDrawerProps {
  opened: boolean;
  onClose: () => void;
  item: AthleteCalendarEventItem | null;
  date: string | null;
}

const coachNames = (coachName: string): string[] =>
  coachName
    .split(',')
    .map(name => name.trim())
    .filter(Boolean);

const DetailRow = ({ label, value }: { label: string; value: string }): JSX.Element => (
  <Group spacing={6} noWrap>
    <Text size='sm' color='dimmed'>
      {label}
    </Text>
    <Text size='sm'>{value}</Text>
  </Group>
);

const ClassDetails = ({ item }: { item: AthleteCalendarEventItem }): JSX.Element => {
  const instructors = coachNames(item.coachName);

  return (
    <Stack spacing={6} data-cy='athleteEventDrawerClassDetails'>
      <Text size='sm' fw={600}>
        Class Details
      </Text>
      <DetailRow label='Time:' value={formatEventTime(item.startTime)} />
      {instructors.length > 0 && (
        <DetailRow
          label={instructors.length === 1 ? 'Instructor:' : 'Instructors:'}
          value={instructors.join(', ')}
        />
      )}
    </Stack>
  );
};

const AppointmentDetails = ({ item }: { item: AthleteCalendarEventItem }): JSX.Element => {
  const coachLabel = formatCoachLabel(item.coachName);

  return (
    <Stack spacing={6}>
      <Text size='sm' color='dimmed'>
        {formatEventTime(item.startTime)}
      </Text>
      {coachLabel && <Text size='sm'>{coachLabel}</Text>}
    </Stack>
  );
};

const AthleteEventDrawer = ({ opened, onClose, item, date }: AthleteEventDrawerProps): JSX.Element => (
  <Drawer
    opened={opened}
    onClose={onClose}
    title={item?.name ?? ''}
    size={DRAWER_WIDTH}
    data-cy='athleteEventDrawer'
  >
    {item && (
      <Stack spacing='md'>
        <Group spacing='xs'>
          <Badge variant='light' color='gray' size='sm'>
            {item.kind === 'appointment' ? 'Appointment' : 'Class Booking'}
          </Badge>
          {date && (
            <Text size='sm' color='dimmed' data-cy='athleteEventDrawerDate'>
              {dayjs(date).format('dddd, MMMM D, YYYY')}
            </Text>
          )}
        </Group>

        {item.kind === 'appointment' ? <AppointmentDetails item={item} /> : <ClassDetails item={item} />}
      </Stack>
    )}
  </Drawer>
);

export default AthleteEventDrawer;
